import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import { TbMessageChatbot } from "react-icons/tb";
import { IoLogoWhatsapp } from "react-icons/io5";
import { FaFacebookMessenger, FaSms } from "react-icons/fa";
import { FaInstagram } from "react-icons/fa6";
import { BiLogoTelegram } from "react-icons/bi";
import {
  MdOutlineLeaderboard,
  MdPhoneInTalk,
  MdHeadset,
  MdHelp,
  MdHtml,
} from "react-icons/md";

ChartJS.register(...registerables);

const CHANNEL_META = {
  chat:               { label: "Chat",             icon: TbMessageChatbot,     color: "#4F46E5" },
  web_call:           { label: "Web Call",         icon: MdPhoneInTalk,        color: "#7C3AED" },
  html_call:          { label: "HTML Call",        icon: MdHtml,               color: "#DB2777" },
  html:               { label: "HTML",             icon: MdHtml,               color: "#BE185D" },
  whatsapp:           { label: "WhatsApp",         icon: IoLogoWhatsapp,       color: "#25D366" },
  telegram:           { label: "Telegram",         icon: BiLogoTelegram,       color: "#229ED9" },
  fb_messenger:       { label: "Messenger",        icon: FaFacebookMessenger,  color: "#0084FF" },
  instagram:          { label: "Instagram",        icon: FaInstagram,          color: "#E1306C" },
  leadconnector:      { label: "LeadConnector",    icon: MdOutlineLeaderboard, color: "#FF6B35" },
  leadconnector_call: { label: "LC Call",          icon: MdOutlineLeaderboard, color: "#FF8C42" },
  sms:                { label: "SMS",              icon: FaSms,                color: "#059669" },
  plivo_call:         { label: "Plivo",            icon: MdHeadset,            color: "#0EA5E9" },
  telnyx_call:        { label: "Telnyx",           icon: MdHeadset,            color: "#00C08B" },
  phone:              { label: "Phone",            icon: MdPhoneInTalk,        color: "#F22F46" },
  sip:                { label: "SIP",              icon: MdPhoneInTalk,        color: "#64748B" },
  render_form:        { label: "Form",             icon: MdHelp,               color: "#D97706" },
  unknown:            { label: "Other",            icon: MdHelp,               color: "#9CA3AF" },
};

function getMeta(channel) {
  return CHANNEL_META[channel] || { label: channel, icon: MdHelp, color: "#9CA3AF" };
}

export default function ChannelDonut({ channelBreakdown }) {
  const total = channelBreakdown.reduce((s, d) => s + d.count, 0);

  if (!total) {
    return (
      <Flex align="center" justify="center" h="200px">
        <Text fontSize="sm" color="gray.400">No channel data yet</Text>
      </Flex>
    );
  }

  const config = {
    data: {
      labels: channelBreakdown.map((d) => getMeta(d.channel).label),
      datasets: [
        {
          data: channelBreakdown.map((d) => d.count),
          backgroundColor: channelBreakdown.map((d) => getMeta(d.channel).color),
          borderWidth: 2,
          borderColor: "#fff",
          hoverOffset: 6,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const pct = ((ctx.parsed / total) * 100).toFixed(1);
              return ` ${ctx.label}: ${ctx.parsed.toLocaleString()} (${pct}%)`;
            },
          },
        },
      },
    },
  };

  return (
    <Flex align="center" gap={4} h="200px">
      {/* Donut */}
      <Box position="relative" h="200px" w="200px" flexShrink={0}>
        <Doughnut {...config} />
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%,-50%)"
          textAlign="center"
          pointerEvents="none"
        >
          <Text fontSize="xl" fontWeight="bold" color="gray.700">{total.toLocaleString()}</Text>
          <Text fontSize="xs" color="gray.400">total</Text>
        </Box>
      </Box>

      {/* Custom icon legend */}
      <Flex direction="column" gap={2} flex={1} overflowY="auto" maxH="200px">
        {channelBreakdown.map((d) => {
          const meta = getMeta(d.channel);
          const pct = ((d.count / total) * 100).toFixed(1);
          return (
            <Flex key={d.channel} align="center" gap={2}>
              <Box
                w="24px"
                h="24px"
                borderRadius="md"
                bg={meta.color}
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Icon as={meta.icon} boxSize="14px" color="white" />
              </Box>
              <Text fontSize="sm" flex={1} color="gray.700" noOfLines={1}>{meta.label}</Text>
              <Text fontSize="xs" color="gray.500" flexShrink={0}>
                {d.count.toLocaleString()}
              </Text>
              <Text fontSize="xs" color="gray.400" w="36px" textAlign="right" flexShrink={0}>
                {pct}%
              </Text>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
}
